import React, {useRef} from 'react';
import {LayoutChangeEvent, Platform, Pressable, View} from 'react-native';
import Config from 'react-native-config';
import {noop} from 'lodash';
import {flow} from 'lodash/fp';

import Button from '/component/base/Button';
import KeyboardFocusable from '/component/base/ExtKeyboard/KeyboardFocusable';
import Icon from '/component/base/Icon';
import BasePopover from '/component/base/Popover';
import Text from '/component/base/Text';
import Gutter from '/component/layout/Gutter';
import {Login} from '/constant/TestIDs';
import {EVENT_NAMES, USER_DECISION} from '/enum/tealium.enum';
import {withTranslation} from '/provider/TranslationProvider';
import {useAccessibilityFocus} from '/util/hooks/useAccessibilityFocus.hook';
import {getTealiumUtilDefaultInstance} from '/util/tealium';

import {styles} from './VisitWebsitePopover.style';

const RESET_PASSWORD_URL = Config.RESET_PASSWORD_URL;

const sendEvent = getTealiumUtilDefaultInstance().generateTrackEvent({
  eventName: EVENT_NAMES.exitLink,
});

const generateOnExitLinkPress =
  ({
    decision,
    destinationURL = RESET_PASSWORD_URL,
    popoverClose,
    onSubmit,
    screenName,
    lastPaidRank,
  }: {
    decision: boolean;
    destinationURL?: string;
    popoverClose: () => void;
    onSubmit: () => void;
    screenName: string;
    lastPaidRank: string;
  }) =>
  () => {
    sendEvent({
      screenName,
      destinationLink: destinationURL,
      userDecision: decision,
      lastPaidRank: lastPaidRank,
    });
    handleButtonPress({popoverClose, onSubmit})();
  };

const handleButtonPress =
  ({
    popoverClose,
    onSubmit,
  }: {
    popoverClose: () => void;
    onSubmit: () => void;
  }) =>
  () => {
    flow([popoverClose, onSubmit])();
  };

interface VisitWebsitePopoverProps {
  tt: (keyPrefix: any, value: any) => string;
  onSubmit: () => void;
  onCancel: () => void;
  popoverClose: () => void;
  currentScreen: string;
  destinationURL: string;
  lastPaidRank: string;
}

const VisitWebsitePopover: React.FC<VisitWebsitePopoverProps> = ({
  tt,
  onSubmit = noop,
  onCancel = noop,
  popoverClose = noop,
  currentScreen = '',
  destinationURL = '',
  lastPaidRank = '',
}) => {
  const stayHereButtonTag = useRef<number | null>(null);
  const stayHereButtonRef = useRef<any>(null);
  const headerNodeRef = useRef<number | null>(null);
  const headerWrapperRef = useRef<View>(null);
  const closeButtonRef = useRef(null);
  // Move these to component scope
  const isScreenReaderEnabled = useAccessibilityFocus(closeButtonRef);
  const headerString =
    typeof tt('title', 'View in Browser') === 'string'
      ? tt('title', 'View in Browser')
      : 'View in Browser';

  const descriptionString =
    typeof tt(
      'description',
      'For the best experience, this link will open in your web browser.',
    ) === 'string'
      ? tt(
          'description',
          'For the best experience, this link will open in your web browser.',
        )
      : 'For the best experience, this link will open in your web browser.';

  const handleStayHereLayout = (evt: LayoutChangeEvent) => {
    // nativeEvent.target is not typed on LayoutChangeEvent, cast to any to read it
    stayHereButtonTag.current = (evt.nativeEvent as any).target ?? null;
  };

  const handleHeaderLayout = (event: LayoutChangeEvent) => {
    headerNodeRef.current = (event.nativeEvent as any).target ?? null;
  };

  return (
    <BasePopover
      padded
      accessible={false}
      accessibilityRole="dialog"
      accessibilityLabel="Visit website pop-up">
      {Platform.OS == 'ios' ? (
        <View>
          <KeyboardFocusable
            viewRef={closeButtonRef}
            style={styles.iconContainer}
            accessible
            accessibilityRole="button"
            accessibilityLabel="Close Pop-up"
            onPress={popoverClose}>
            <Icon name="close" style={styles.closeIcon} />
          </KeyboardFocusable>
        </View>
      ) : (
        <View accessible={!isScreenReaderEnabled}>
          <Pressable
            ref={closeButtonRef}
            style={styles.iconContainer}
            accessible
            accessibilityRole="button"
            accessibilityLabel="Close Pop-up"
            onPress={popoverClose}>
            <Icon name="close" style={styles.closeIcon} />
          </Pressable>
        </View>
      )}

      <View
        ref={headerWrapperRef}
        onLayout={handleHeaderLayout}
        accessible={true}
        accessibilityRole="header"
        accessibilityLabel={headerString}>
        <Text
          testID={Login.loginModalHeader}
          style={styles.header}
          variant="large">
          {headerString}
        </Text>
      </View>

      <Text
        testID={Login.loginModalBody}
        accessible={true}
        accessibilityRole="text"
        style={styles.text}>
        {descriptionString}
      </Text>

      <View style={styles.buttonContainer} accessible={false}>
        <Button
          accessible
          accessibilityRole="button"
          testID="login-modal-stay-here"
          accessibilityLabel="Stay here"
          type="secondary"
          style={{
            base: {...styles.button, ...styles.cancelButton},
            text: styles.buttonText,
          }}
          onLayout={handleStayHereLayout}
          buttonRef={stayHereButtonRef}
          onPress={generateOnExitLinkPress({
            screenName: currentScreen,
            decision: USER_DECISION.false,
            destinationURL,
            popoverClose,
            onSubmit: onCancel,
            lastPaidRank,
          })}>
          {tt('cancelLabel', 'Stay here')}
        </Button>
        <Gutter />
        <Button
          accessible={true}
          testID="login-modal-go-to-site"
          accessibilityRole="button"
          accessibilityLabel="Leave app"
          style={{
            base: {...styles.button, ...styles.mainButton},
            text: styles.buttonText,
          }}
          onPress={generateOnExitLinkPress({
            screenName: currentScreen,
            decision: USER_DECISION.true,
            destinationURL,
            popoverClose,
            onSubmit,
            lastPaidRank,
          })}>
          {tt('submitLabel', 'Leave app')}
        </Button>
      </View>
    </BasePopover>
  );
};

export default withTranslation('visitWebsiteModal')(VisitWebsitePopover);
